import React, { useCallback, useEffect, useMemo } from 'react';
import { useAuthNavigation } from '../../../../shared/navigation/useAuthNavigation';
import { View, Text, StyleSheet, Platform, type TextStyle } from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withRepeat,
  withSequence,
  withTiming,
  Easing,
  cancelAnimation,
} from 'react-native-reanimated';
import moment from 'moment';
import { useTranslation, formatNumber, formatPercent, getLocale } from '../../../../shared/i18n';
import { horizontalLtrDirectionStyle } from '../../../../shared/i18n/rtl';
import { useRtl } from '../../../../shared/i18n/useRtl';
import type { ThemeColors } from '../../../../shared/theme';
import { Clickable } from '../../../../components/common/Clickable';
import { SvgIcon } from '../../../../components/common/SvgIcon';
import { appTheme } from '../../../../shared/theme';
import { hexToRgba } from '../../../../shared/theme/colorUtils';
import { useHealthInformationCms } from '../../../../healthInformation/components/HealthInformationCmsContext';
import { useCaloriesLimitVisible } from '../../../../data/domains/config';
import {
  normalizePlanCalorieLimit,
  resolveDashboardCalorieGoal,
  resolvePurchasedSubscriptionCalorieRange,
  useSubscriptionContext,
} from '../../../../data/domains/subscription';
import { DASH_STUDIO_INNER } from '../../../../shared/theme/studio/dashboardConstants';
import { dashboardNestedTileShadow, dashboardSet1CardShell } from './dashboardSet1Styles';
import { DashboardSet1EmptyState } from './DashboardSet1EmptyState';

interface MacroTotals {
  calories: number;
  protein: number;
  carbs: number;
  fats: number;
}

interface DayConfigStub {
  min_calories?: number;
  max_calories?: number;
}

export interface DashboardNutritionCardSet1Props {
  macro_totals: MacroTotals;
  day_config: DayConfigStub | null;
  selected_date: string;
  colors: ThemeColors;
  is_empty: boolean;
}

type CalorieRangeStatus = 'over' | 'under' | 'ok';

const ANDROID_TEXT: TextStyle = Platform.OS === 'android' ? { includeFontPadding: false } : {};

function resolveCalorieFloor(options: {
  calories_limit_enabled: boolean;
  day_config: DayConfigStub | null;
  subscription: Parameters<typeof resolvePurchasedSubscriptionCalorieRange>[0];
}): number {
  if (!options.calories_limit_enabled) {
    return 0;
  }
  const from_day = normalizePlanCalorieLimit(options.day_config?.min_calories);
  if (from_day > 0) {
    return from_day;
  }
  return normalizePlanCalorieLimit(resolvePurchasedSubscriptionCalorieRange(options.subscription).min_calories);
}

function resolveCalorieRangeStatus(calories: number, cal_min: number, cal_max: number): CalorieRangeStatus | null {
  if (cal_max <= 0 && cal_min <= 0) {
    return null;
  }
  if (cal_max > 0 && calories > cal_max) {
    return 'over';
  }
  if (cal_min > 0 && calories < cal_min) {
    return 'under';
  }
  return 'ok';
}

/** Signed % vs range edge: over max → positive excess; under min → negative deficit. */
function resolveCalorieDeltaPct(
  calories: number,
  cal_min: number,
  cal_max: number,
  status: CalorieRangeStatus | null,
): number | null {
  if (status === 'over' && cal_max > 0) {
    return Math.round(((calories - cal_max) / cal_max) * 100);
  }
  if (status === 'under' && cal_min > 0) {
    return -Math.round(((cal_min - calories) / cal_min) * 100);
  }
  return null;
}

function CalorieStatusBadge({
  status,
  delta_pct,
  colors,
}: {
  status: CalorieRangeStatus;
  delta_pct: number | null;
  colors: ThemeColors;
}) {
  const progress = useSharedValue(0);

  useEffect(() => {
    if (status === 'ok') {
      cancelAnimation(progress);
      progress.value = 0;
      return;
    }
    progress.value = 0;
    progress.value = withRepeat(
      withSequence(
        withTiming(1, { duration: 1600, easing: Easing.inOut(Easing.quad) }),
        withTiming(0, { duration: 1600, easing: Easing.inOut(Easing.quad) }),
      ),
      -1,
      false,
    );
    return () => {
      cancelAnimation(progress);
    };
  }, [status, progress]);

  const animated_style = useAnimatedStyle(() => {
    const t = progress.value;
    const y_dir = status === 'under' ? 1 : -1;
    return {
      opacity: 0.45 + 0.55 * (1 - t),
      transform: [{ translateY: y_dir * 5 * t }, { scale: 1 + 0.12 * t }],
    };
  });

  const tone = status === 'over' ? colors.error : status === 'under' ? colors.warning : colors.success;
  const icon_name = status === 'over' ? 'arrow-up' : status === 'under' ? 'arrow-down' : 'check';
  const show_delta = status !== 'ok' && delta_pct != null && delta_pct !== 0;
  const delta_label = show_delta ? `${delta_pct! > 0 ? '+' : ''}${formatPercent(delta_pct!)}` : null;
  const a11y =
    status === 'over'
      ? `Above calorie range${delta_label ? `, ${delta_label}` : ''}`
      : status === 'under'
        ? `Below calorie range${delta_label ? `, ${delta_label}` : ''}`
        : 'Within calorie range';

  return (
    <View style={styles.statusInner}>
         {delta_label ? (
          <Text style={[styles.statusDelta, { color: tone }, ANDROID_TEXT]}>{delta_label}</Text>
        ) : null}
    <Animated.View
      style={[styles.statusBadge, status !== 'ok' ? animated_style : null]}
      accessible
      accessibilityRole="image"
      accessibilityLabel={a11y}>
        <SvgIcon name={icon_name} size={24} color={tone} />
    </Animated.View>
    </View>
  );
}

function MacroColumn({
  label,
  grams,
  share_pct,
  color,
  colors,
  writing_direction,
  show_divider,
}: {
  label: string;
  grams: number;
  share_pct: number;
  color: string;
  colors: ThemeColors;
  writing_direction: 'ltr' | 'rtl';
  show_divider: boolean;
}) {
  const divider_color = hexToRgba(colors.text, 0.08) as string;

  return (
    <>
      {show_divider ? <View style={[styles.colDivider, { backgroundColor: divider_color }]} /> : null}
      <View style={styles.macroCol}>
        <View style={styles.labelRow}>
          <View style={[styles.dot, { backgroundColor: color }]} />
          <Text style={[styles.colLabel, { color: colors.textSecondary, writingDirection: writing_direction }, ANDROID_TEXT]} numberOfLines={1}>
            {label}
          </Text>
        </View>
        <View style={styles.valueRow}>
          <Text style={[styles.colValue, { color: colors.text, writingDirection: writing_direction }, ANDROID_TEXT]}>{formatNumber(grams)}</Text>
          <Text style={[styles.colUnit, { color: colors.textLight, writingDirection: writing_direction }, ANDROID_TEXT]}>g</Text>
        </View>
        <Text style={[styles.colPct, { color, writingDirection: writing_direction }, ANDROID_TEXT]}>{formatPercent(share_pct)}</Text>
      </View>
    </>
  );
}

export function DashboardNutritionCardSet1({
  macro_totals,
  day_config,
  selected_date,
  colors,
  is_empty,
}: DashboardNutritionCardSet1Props) {
  const { t } = useTranslation();
  const navigation = useAuthNavigation();
  const rtl = useRtl();
  const { isAvailable: health_info_available } = useHealthInformationCms();
  const calories_limit_enabled = useCaloriesLimitVisible();
  const { activeSubscriptions } = useSubscriptionContext();
  const text_align = rtl.textAlign;
  const writing_direction = rtl.writingDirection;
  const active_sub = activeSubscriptions[0] ?? null;

  const { calories, protein, carbs, fats } = macro_totals;
  const cal_goal = resolveDashboardCalorieGoal({
    calories_limit_enabled,
    day_config,
    subscription: active_sub,
  });
  const cal_min = resolveCalorieFloor({
    calories_limit_enabled,
    day_config,
    subscription: active_sub,
  });
  const range_status = resolveCalorieRangeStatus(calories, cal_min, cal_goal);
  const delta_pct = resolveCalorieDeltaPct(calories, cal_min, cal_goal, range_status);

  const locale = getLocale();
  const date_label = selected_date
    ? (() => {
        try {
          moment.locale(locale === 'ar' ? 'ar' : 'en');
          const m = moment(selected_date, 'YYYY-MM-DD', true);
          return `${m.format('MMM')} ${formatNumber(m.date())}`;
        } catch {
          moment.locale('en');
          const m = moment(selected_date, 'YYYY-MM-DD', true);
          return `${m.format('MMM')} ${formatNumber(m.date())}`;
        }
      })()
    : t('dashboard.today');

  const fill = colors.calorie || colors.primary;
  const track = hexToRgba(colors.text, 0.07) as string;
  const cal_pct = cal_goal > 0 ? Math.round((calories / cal_goal) * 100) : 0;
  const cal_bar_w = `${Math.min(100, Math.max(0, cal_pct))}%` as const;

  const open_health_sources = useCallback(() => {
    (navigation as { navigate: (name: 'HealthInformationSources') => void }).navigate('HealthInformationSources');
  }, [navigation]);

  const macro_pcts = useMemo(() => {
    const total_g = protein + carbs + fats;
    if (total_g <= 0) {
      return { p: 0, c: 0, f: 0 };
    }
    return {
      p: Math.round((protein / total_g) * 100),
      c: Math.round((carbs / total_g) * 100),
      f: Math.round((fats / total_g) * 100),
    };
  }, [protein, carbs, fats]);

  if (is_empty) {
    return (
      <DashboardSet1EmptyState
        colors={colors}
        icon="flame"
        icon_color={fill}
        title={t('dashboard.todaysMacros')}
        message={t('dashboard.selectMealsHint').replace('{{date}}', date_label)}
        cta_label={t('dashboard.exploreDishes')}
        on_cta={() => navigation.navigate('DailyMenuFlow')}
      />
    );
  }

  return (
    <View style={[dashboardSet1CardShell(colors), styles.card]}>
      <View style={styles.headerRow}>
        <View style={styles.headerText}>
          <Text
            style={[styles.overline, { color: colors.textLight, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}
            accessibilityRole="header">
            {t('dashboard.dailyNutrition')}
          </Text>
          <Text style={[styles.dateSub, { color: colors.textSecondary, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}>
            {date_label}
          </Text>
        </View>
        {health_info_available ? (
          <Clickable
            onPress={open_health_sources}
            style={[styles.healthBtn, { backgroundColor: hexToRgba(colors.text, 0.05) as string }]}
            accessibilityLabel={t('dashboard.macrosHealthSourcesAccessibility')}
            accessibilityRole="button"
            haptics>
            <Text style={[styles.healthMark, { color: colors.primary }, ANDROID_TEXT]}>!</Text>
          </Clickable>
        ) : null}
      </View>

      <View style={styles.heroBlock}>
        <View style={[styles.heroTopRow, rtl.flipRowStyle]}>
          <View style={[styles.heroRow, horizontalLtrDirectionStyle]}>
            <Text style={[styles.heroKcal, { color: colors.text, writingDirection: writing_direction }, ANDROID_TEXT]}>
              {formatNumber(calories)}
            </Text>
            <Text style={[styles.heroUnit, { color: colors.textSecondary, writingDirection: writing_direction }, ANDROID_TEXT]}>
              {t('dashboard.kcal')}
            </Text>
          </View>
          {range_status ? (
            <CalorieStatusBadge status={range_status} delta_pct={delta_pct} colors={colors} />
          ) : null}
        </View>
        <View style={[styles.calTrack, horizontalLtrDirectionStyle, { backgroundColor: track }]}>
          <View style={[styles.calFill, { width: cal_bar_w, backgroundColor: fill }]} />
        </View>
        {cal_goal > 0 ? (
          <Text style={[styles.goalHint, { color: colors.textLight, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}>
            {t('dashboard.nutritionGoalHint', { pct: cal_pct, goal: cal_goal })}
          </Text>
        ) : null}
      </View>

      <View style={[styles.macroTray, dashboardNestedTileShadow(colors), { backgroundColor: colors.surfaceElevated }]}>
        <View style={[styles.macroRow, horizontalLtrDirectionStyle]}>
          <MacroColumn
            label={t('dashboard.protein')}
            grams={protein}
            share_pct={macro_pcts.p}
            color={colors.protein}
            colors={colors}
            writing_direction={writing_direction}
            show_divider={false}
          />
          <MacroColumn
            label={t('dashboard.carbs')}
            grams={carbs}
            share_pct={macro_pcts.c}
            color={colors.carbs}
            colors={colors}
            writing_direction={writing_direction}
            show_divider
          />
          <MacroColumn
            label={t('dashboard.fat')}
            grams={fats}
            share_pct={macro_pcts.f}
            color={colors.fats}
            colors={colors}
            writing_direction={writing_direction}
            show_divider
          />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    padding: DASH_STUDIO_INNER,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: appTheme.spacing.sm,
    marginBottom: appTheme.spacing.md,
  },
  headerText: { flex: 1, minWidth: 0, gap: 2 },
  overline: {
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.9,
    textTransform: 'uppercase',
  },
  dateSub: { fontSize: 12, fontWeight: '600' },
  healthBtn: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  healthMark: { fontSize: 12, fontWeight: '800' },
  heroBlock: {
    alignItems: 'stretch',
    marginBottom: appTheme.spacing.md,
    gap: appTheme.spacing.sm,
  },
  heroTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: appTheme.spacing.md,
  },
  heroRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: appTheme.spacing.xs,
    flexShrink: 1,
    minWidth: 0,
  },
  heroKcal: {
    fontSize: 36,
    fontWeight: '800',
    letterSpacing: -1.2,
    lineHeight: 38,
  },
  heroUnit: {
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.45,
    marginBottom: 3,
  },
  statusBadge: {
    minWidth: 36,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  statusInner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
  },
  statusDelta: {
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: -0.2,
    lineHeight: 13,
  },
  calTrack: {
    height: 5,
    borderRadius: 3,
    overflow: 'hidden',
  },
  calFill: {
    height: '100%',
    borderRadius: 3,
    minWidth: 4,
  },
  goalHint: {
    fontSize: 11,
    fontWeight: '500',
    lineHeight: 15,
  },
  macroTray: {
    borderRadius: 14,
    paddingVertical: appTheme.spacing.sm + 2,
  },
  macroRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  colDivider: {
    width: StyleSheet.hairlineWidth,
    alignSelf: 'stretch',
  },
  macroCol: {
    flex: 1,
    minWidth: 0,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: appTheme.spacing.xxs,
    gap: 4,
  },
  labelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 5,
    maxWidth: '100%',
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    flexShrink: 0,
  },
  colLabel: {
    fontSize: 10,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.35,
    flexShrink: 1,
  },
  valueRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'center',
    gap: 1,
  },
  colValue: {
    fontSize: 18,
    fontWeight: '800',
    letterSpacing: -0.5,
    lineHeight: 20,
  },
  colUnit: {
    fontSize: 11,
    fontWeight: '700',
    lineHeight: 14,
    marginBottom: 1,
  },
  colPct: {
    fontSize: 11,
    fontWeight: '700',
    lineHeight: 13,
  },
});
